/**
 * Pricing page — Starter / Professional / Business / Enterprise tiers.
 * Monthly / Annual toggle (annual = 2 months free).
 */
import { useState, useEffect } from "react";
import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Sparkles, ShieldCheck, HelpCircle, Flame } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const API = process.env.REACT_APP_BACKEND_URL;

// Live scarcity — hits /api/public/first-closer-count and shows how many seats left.
function FirstCloserCounter() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/first-closer-count`)
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  const { filled, total, remaining, closed } = state;

  if (closed) {
    return (
      <div
        data-testid="first-closer-counter"
        className="mt-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 bg-slate-900 text-white font-bold text-[13px] shadow-md"
      >
        <Flame className="w-3.5 h-3.5 text-amber-400" />
        First Closer cohort is closed · Waitlist open
      </div>
    );
  }

  // Urgent styling once we're <25% remaining
  const urgent = remaining <= 25;
  return (
    <div
      data-testid="first-closer-counter"
      className={`mt-4 inline-flex items-center gap-2.5 rounded-full px-3.5 py-1.5 border font-bold text-[13px] shadow-sm transition-all ${
        urgent
          ? "bg-rose-50 border-rose-300 text-rose-900 ring-2 ring-rose-200/60"
          : "bg-emerald-50 border-emerald-300 text-emerald-900"
      }`}
    >
      <Flame className={`w-3.5 h-3.5 ${urgent ? "text-rose-600" : "text-emerald-600"}`} />
      <span>
        First Closer #{filled + 1} of {total} ·{" "}
        <span className="tabular-nums">{remaining}</span> seat{remaining === 1 ? "" : "s"} left
      </span>
      <span className="hidden md:inline text-[11px] font-medium opacity-80">
        First Closer pricing locked for life
      </span>
    </div>
  );
}

const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
    {children}
  </div>
);

const Section = ({ children, bg = "paper", tid }) => {
  const bgClass = bg === "panel"
    ? "bg-white text-slate-900"
    : bg === "paper"
    ? "bg-[#FAFAF7] text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={bgClass}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">{children}</div>
    </section>
  );
};

// Tier accent colours (hex — bypasses global emerald→amber theme mapping).
const ACCENT = {
  free:    { dot: "#94a3b8", ring: "ring-slate-500", chipBg: "#f1f5f9", chipText: "#334155" },
  starter: { dot: "#f59e0b", ring: "ring-amber-500", chipBg: "#fef3c7", chipText: "#78350f" },
  basic:   { dot: "#4f46e5", ring: "ring-indigo-500", chipBg: "#eef2ff", chipText: "#3730a3" },
  pro:     { dot: "#3b82f6", ring: "ring-blue-500", chipBg: "#eff6ff", chipText: "#1e40af" },
  team:    { dot: "#a855f7", ring: "ring-purple-500", chipBg: "#faf5ff", chipText: "#6b21a8" },
};

const TIERS = [
  {
    slug: "starter",
    name: "Starter",
    subtitle: "Real CRM for less than half of REsimpli. BYO Twilio, no markup, first envelope free.",
    monthly: 47,
    annual: 470,
    ctaLabel: "Start 14-day free trial",
    ctaTo: "/auth?mode=signup&plan=starter",
    badge: "vs REsimpli $99",
    features: [
      "Up to 25 active properties + Pipeline CRM",
      "Two-way SMS + Click-to-Call (BYO Twilio)",
      "Always-On Recording",
      "5 BatchData skip-trace credits / month",
      "1 DocuSign envelope / mo — or BYO DocuSign (unlimited)",
      "3 Shareable Pitch Links (no watermark)",
      "Jarvis AI — 5 messages / day",
      "Daily Briefing email (Mon-Fri)",
      "Lead Marketplace browse",
      "TCPA-Safe DNC screening",
      "Email support",
    ],
  },
  {
    slug: "basic",
    name: "Growth",
    subtitle: "Solo wholesaler scaling to 3–5 deals/mo. Full CRM, all the essentials.",
    monthly: 97,
    annual: 970,
    ctaLabel: "Start 14-day free trial",
    ctaTo: "/auth?mode=signup&plan=basic",
    badge: "vs REsimpli $199",
    features: [
      "Everything in Starter, plus:",
      "Up to 50 active properties",
      "10 BatchData skip-trace credits / month",
      "3 DocuSign envelopes / mo (or BYO DocuSign)",
      "5 Shareable Pitch Links + OG images",
      "Jarvis AI — 10 messages / day",
      "Instant email alerts",
      "List-stacking view",
      "Live DocuSign Quota Widget",
      "Email drip campaign (1 active)",
    ],
  },
  {
    slug: "pro",
    name: "Pro",
    subtitle: "The AI operating system. Full Deal Intelligence + Jarvis unlimited.",
    monthly: 197,
    annual: 1970,
    highlighted: true,
    badge: "⭐ Most Popular · vs REsimpli $299",
    ctaLabel: "Start 14-day free trial",
    ctaTo: "/auth?mode=signup&plan=pro",
    features: [
      "Everything in Growth, plus:",
      "Unlimited active properties",
      "🔥 AI Deal Intelligence Trilogy (MAO · Rehab · Comps)",
      "📊 6 Recharts Deal Intelligence Visualizations",
      "🤖 Jarvis AI — Unlimited + British Voice (ElevenLabs)",
      "🎯 AI Lead Scoring 0-100 with reasoning",
      "📞 AI Call Summary + 0-100 Coach Score",
      "📝 AI Cold-Call Script Generator",
      "🔍 AI Rehab Scan — 10/day (Claude Vision)",
      "50 DocuSign envelopes / mo (or BYO for unlimited)",
      "Unlimited Shareable Pitch Links",
      "Real-time SMS alerts + AI verdict",
      "Distress filters + auto-enrichment",
      "Unlimited drips + Sequences (with AI personalization)",
      "150 BatchData skip-trace credits / month",
      "Daily Briefing + Evening Wrap emails",
      "🏛 Perrin Insider community + weekly office hours",
      "📄 Monthly auto-generated Portfolio PDF (investor-ready)",
      "Affiliate program (45% recurring)",
      "Priority email support (24h SLA)",
    ],
  },
  {
    slug: "team",
    name: "Elite",
    subtitle: "For brokerages + acquisition teams. Everything, plus seats, shared pipelines & API.",
    monthly: 397,
    annual: 3970,
    ctaLabel: "Start 14-day free trial",
    ctaTo: "/auth?mode=signup&plan=team",
    badge: "vs REsimpli $499",
    features: [
      "Everything in Pro, plus:",
      "5 team seats included · +$39/seat · +$19/VA",
      "200 DocuSign envelopes / mo + BYO OAuth",
      "Unlimited Pitch Links + Custom domain",
      "Jarvis AI — Unlimited + Priority queue",
      "400 BatchData skip-trace credits / month",
      "30/day AI Rehab Scans",
      "Team Analytics dashboard",
      "Role-based permissions (Owner/Admin/Member/VA)",
      "Shared pipeline + Shared workspace lists",
      "Custom branded sender domain",
      "API access",
      "Dedicated Slack + 4h SLA + Onboarding call",
    ],
  },
];

const COMPARE_MATRIX = [
  { section: "Core Property Ops", rows: [
    ["Immutable Timeline", true, true, true, true],
    ["Workspace Tab Plugins", true, true, true, true],
    ["Active properties", "25", "50", "Unlimited", "Unlimited"],
    ["BYO Twilio (SMS + Dialer + Recording)", true, true, true, true],
    ["BatchData skip-trace credits / mo", "5", "10", "150", "400"],
  ]},
  { section: "DocuSign eSignature", rows: [
    ["Envelopes / month (via app)", "1", "3", "50", "200"],
    ["BYO DocuSign account (unlimited)", true, true, true, true],
    ["Live DocuSign Quota Widget", true, true, true, true],
  ]},
  { section: "Shareable Pitch Links", rows: [
    ["Max Pitch Links", "3", "5", "Unlimited", "Unlimited"],
    ["Auto-generated OG images", true, true, true, true],
    ["Watermark removed", true, true, true, true],
    ["Custom domain (pitch.yourbrand.com)", false, false, false, true],
  ]},
  { section: "AI Deal Intelligence", rows: [
    ["Deal Intelligence Trilogy (MAO/Rehab/Comps)", false, false, true, true],
    ["6 Recharts Visualizations", false, false, true, true],
    ["AI Lead Scoring 0-100", false, false, true, true],
    ["AI Call Summary + Coach Score", false, false, true, true],
    ["AI Cold-Call Script Generator", false, false, true, true],
    ["AI Rehab Scan / day", "0", "0", "10", "30"],
    ["AI-Vetted Deals Inbox", false, false, true, true],
  ]},
  { section: "Jarvis — AI Operating Partner", rows: [
    ["Daily message quota", "5", "10", "Unlimited", "Unlimited + priority"],
    ["British voice output (ElevenLabs)", false, false, true, true],
    ["Daily Briefing (Mon-Fri 7am)", true, true, true, true],
    ["Evening Wrap (7pm)", false, false, true, true],
  ]},
  { section: "Alerts & Outreach", rows: [
    ["Instant email alerts", true, true, true, true],
    ["Real-time SMS alerts + AI verdict", false, false, true, true],
    ["Distress filters (foreclosure/tax-delinq)", false, false, true, true],
    ["Drip campaigns", "1 (no AI)", "1 (no AI)", "Unlimited + AI", "Unlimited + AI"],
    ["Multi-channel Sequences (SMS+Email+Tasks)", "0", "0", "Unlimited", "Unlimited"],
  ]},
  { section: "Community & Growth", rows: [
    ["Perrin Insider community + weekly office hours", false, false, true, true],
    ["Monthly auto-generated Portfolio PDF", false, false, true, true],
    ["Affiliate program (45% recurring)", false, false, true, true],
  ]},
  { section: "Team & Collaboration", rows: [
    ["Team seats included", "1", "1", "1", "5 (+$39/ea)"],
    ["VA seats", false, false, false, "$19/seat/mo"],
    ["Team Analytics dashboard", false, false, false, true],
    ["Role-based permissions", false, false, false, true],
    ["Shared workspace lists + Power Team", false, false, false, true],
    ["Custom branded sender domain", false, false, false, true],
    ["API access", false, false, false, true],
  ]},
  { section: "Support", rows: [
    ["Email support", true, true, true, true],
    ["Priority email (24h SLA)", false, false, true, true],
    ["Dedicated Slack (4h SLA) + Onboarding", false, false, false, true],
  ]},
];

const FAQ = [
  { q: "Is there a free plan?", a: "There's no free tier anymore — every plan starts with a 14-day free trial (no credit card required). Free tiers train people to expect free, and we built this to make you money, not to be a hobby. The Starter plan at $47 is designed to cost less than one skip-trace list from REsimpli." },
  { q: "How does the free trial work?", a: "Every new signup gets 14 days of full Pro access automatically — AI Deal Intelligence Trilogy, unlimited Jarvis, British voice output, sequences, distress filters, the works. No credit card required. On day 15 you keep whatever tier you pick — or you cancel with one click." },
  { q: "How does annual billing work?", a: "Annual plans give you effectively 2 months free — Starter is $470/yr (saves $94), Growth is $970/yr (saves $194), Pro is $1,970/yr (saves $394), Elite is $3,970/yr (saves $794)." },
  { q: "Why should I switch from REsimpli?", a: "REsimpli starts at $99 for their Basic and jumps to $499 for Team Pro. We start at $47 and top out at $397. Same all-in-one dispatch: CRM, Twilio SMS, Docs, AI. Plus we give you the AI Deal Intelligence Trilogy (MAO / Rehab / Comps auto-analysis) on Pro — which REsimpli doesn't ship. Full head-to-head: /vs/resimpli." },
  { q: "Can I change plans later?", a: "Yes. Upgrade or downgrade anytime — changes prorate on the next billing cycle. Downgrade auto-caps your usage to the new tier." },
  { q: "What's the difference between DocuSign 'via app' and 'BYO'?", a: "Every paid tier lets you connect your own DocuSign account (BYO) via OAuth for unlimited sending on your quota. The 'via app' number (1/3/50/200) is a fallback pool that uses the platform's DocuSign — great to try the feature before connecting your own." },
  { q: "What powers Jarvis?", a: "Jarvis runs on Claude 4.5 Sonnet with a live snapshot of your workspace. On Pro+, replies are spoken back with ElevenLabs British voice — like Iron Man's Jarvis, but for real estate ops. Plus Pro gets 7am Daily Briefing AND 7pm Evening Wrap emails automatically." },
  { q: "What are Pitch Links?", a: "Every property can be flipped 'public' to generate a shareable URL like ispeedtoclose.com/pitch/1247-oak-street-atlanta-ga. Cash buyers see the deal, the numbers, and can inquire — no login. Each share is tracked (views + inquiries) so you know which pitches are hot." },
  { q: "What is the Perrin Insider community?", a: "A Pro+ private community with weekly office hours hosted by our lead coach — real deal reviews, tactic swaps, and hot-lead sharing. Only unlocked at Pro tier and up." },
  { q: "Can I make a living on Starter or Growth?", a: "Starter ($47) is enough to run 1-2 test deals — real CRM, real SMS, real contracts. Growth ($97) covers a solo wholesaler doing 3-5 deals/month. Once you're closing 5+ deals/month, Pro pays for itself with the first AI-analyzed deal you don't miss." },
];

const priceDisplay = (tier, cycle) => {
  if (tier.monthly === 0) {
    return { amount: "$0", suffix: "/mo", note: "Free forever" };
  }
  if (cycle === "annual") {
    const monthlyEquivalent = Math.round(tier.annual / 12);
    return { amount: `$${tier.annual.toLocaleString()}`, suffix: "/yr", note: `≈ $${monthlyEquivalent}/mo · 2 months free` };
  }
  return { amount: `$${tier.monthly}`, suffix: "/mo", note: null };
};

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 text-emerald-600 mx-auto" data-testid="cell-yes" />;
  if (v === false) return <X className="w-4 h-4 text-slate-300 mx-auto" data-testid="cell-no" />;
  return <span className="text-sm font-mono-x text-slate-800">{v}</span>;
};

export default function PricingPage() {
  const [cycle, setCycle] = useState("monthly");

  return (
    <div className="min-h-screen exos" data-testid="pricing-page">
      <MarketingHeader />

      {/* HERO — cinematic obsidian with radial spotlight + grain */}
      <section
        data-testid="pricing-hero"
        className="relative overflow-hidden exos-spotlight exos-noise"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="exos-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Pricing · Undercut REsimpli. Ship more deals.
            </span>
            <h1
              data-testid="pricing-hero-headline"
              className="font-display mt-6 text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900"
            >
              Half the price.<br />
              Twice the AI.<br />
              <span className="text-emerald-600">Same all-in-one dispatch.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
              REsimpli starts at $99. iSpeedToClose starts at <strong className="text-slate-900">$47</strong>.
              Every plan runs the same Deal Intelligence · Jarvis · Timeline engine —
              plus the AI Deal Intelligence Trilogy (MAO / Rehab / Comps) that REsimpli doesn&rsquo;t ship at any price.
              Pick the tier that matches how many deals you close per month.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm">
              {/* Starburst — 14-day Pro trial is the primary conversion anchor */}
              <span
                data-testid="pricing-trial-badge"
                className="relative inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 font-extrabold text-[13px] shadow-md ring-2 ring-amber-300/50"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Free 14-day Pro trial
                <span className="absolute -inset-1 rounded-full bg-amber-300/30 blur-sm animate-pulse pointer-events-none" />
              </span>
              <span className="text-slate-600">No credit card required</span>
            </div>

            {/* Live First Closer scarcity counter — social proof + urgency */}
            <FirstCloserCounter />

            {/* CYCLE TOGGLE */}
            <div
              data-testid="pricing-cycle-toggle"
              className="mt-10 inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 p-1 shadow-sm"
            >
              <button
                type="button"
                onClick={() => setCycle("monthly")}
                data-testid="pricing-cycle-monthly"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all ${
                  cycle === "monthly"
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setCycle("annual")}
                data-testid="pricing-cycle-annual"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all inline-flex items-center gap-2 ${
                  cycle === "annual"
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Annual
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-950 bg-emerald-400 rounded-full px-2 py-0.5">
                  2 mo free
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS — dark hairline cards, Pro gets the tracing beam */}
      <Section tid="pricing-tiers" bg="obsidian">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => {
            const accent = ACCENT[t.slug];
            const price = priceDisplay(t, cycle);
            const isHighlighted = t.highlighted;

            const cardInner = (
                <div
                  data-testid={`pricing-tier-${t.slug}`}
                  className={`relative rounded-2xl p-7 flex flex-col h-full transition-all ${
                    isHighlighted
                      ? "bg-white border border-emerald-500/30 shadow-2xl shadow-emerald-500/10"
                      : "exos-card hover:border-slate-700"
                  }`}
                >
                  {t.badge && (
                    <div
                      data-testid={`pricing-badge-${t.slug}`}
                      className="absolute -top-3 left-7 z-20 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 shadow-md whitespace-nowrap"
                      style={{ backgroundColor: accent.dot, color: "#0f172a" }}
                    >
                      {t.badge}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: accent.dot, boxShadow: `0 0 8px ${accent.dot}` }}
                      aria-hidden="true"
                    />
                    <div className="font-display text-2xl font-bold tracking-tight text-slate-900">{t.name}</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-600 min-h-[52px] leading-relaxed">{t.subtitle}</div>

                  <div className="mt-6 flex items-baseline gap-1.5" data-testid={`pricing-price-${t.slug}`}>
                    <div className="font-display text-5xl font-black text-slate-900 tracking-tighter">{price.amount}</div>
                    <div className="text-sm text-slate-500 font-mono-x">{price.suffix}</div>
                  </div>
                  {price.note && (
                    <div
                      className="text-xs font-semibold mt-1 text-emerald-600"
                      data-testid={`pricing-note-${t.slug}`}
                    >
                      {price.note}
                    </div>
                  )}

                  <Link
                    to={t.ctaTo}
                    data-testid={`pricing-cta-${t.slug}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full font-bold px-6 py-3 text-sm transition-all ${
                      isHighlighted
                        ? "exos-btn-primary"
                        : "exos-btn-ghost"
                    }`}
                  >
                    {t.ctaLabel} <ArrowRight className="w-4 h-4" />
                  </Link>

                  <ul className="mt-7 space-y-2.5 text-sm text-slate-700">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: accent.dot }}
                        />
                        <span className="leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
            );

            return isHighlighted ? (
              <div key={t.slug} className="exos-trace">{cardInner}</div>
            ) : (
              <div key={t.slug}>{cardInner}</div>
            );
          })}
        </div>
      </Section>

      {/* vs REsimpli — direct competitor callout */}
      <section
        data-testid="pricing-vs-resimpli"
        className="bg-gradient-to-br from-emerald-50 via-white to-amber-50 border-y border-emerald-200/60"
      >
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
                Head-to-head
              </div>
              <h2 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tighter text-slate-900">
                iSpeedToClose vs.<br />
                <span className="text-emerald-600">REsimpli.</span>
              </h2>
              <p className="mt-5 text-slate-700 leading-relaxed">
                REsimpli is the most direct competitor in this category. They're solid at telephony
                and RVM — we lead on AI Deal Intelligence, Community Wall dispositions,
                and the Community-to-DocuSign auto-envelope pipeline they've never shipped.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/vs/resimpli"
                  data-testid="pricing-vs-resimpli-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-6 py-3 text-sm shadow-md hover:bg-slate-800 transition-all"
                >
                  See the full comparison <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://resimpli.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-700 font-semibold px-6 py-3 text-sm hover:bg-slate-50 transition-all"
                >
                  REsimpli's site
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { plan: "Basic entry", them: "$99", us: "$47" },
                { plan: "Growth tier", them: "$199", us: "$97" },
                { plan: "Pro / Enterprise", them: "$299", us: "$197" },
                { plan: "Team Pro / Elite", them: "$499", us: "$397" },
              ].map((row) => (
                <React.Fragment key={row.plan}>
                  <div className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{row.plan}</div>
                    <div className="mt-1 text-xs text-slate-400">REsimpli</div>
                    <div className="mt-1 text-2xl font-black text-slate-400 line-through tabular-nums">{row.them}</div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 shadow-lg">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-100 font-bold">{row.plan}</div>
                    <div className="mt-1 text-xs text-emerald-100">iSpeedToClose</div>
                    <div className="mt-1 text-2xl font-black tabular-nums">{row.us}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON MATRIX */}
      <Section tid="pricing-compare" bg="panel">
        <span className="exos-eyebrow">Feature Comparison</span>
        <h2 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tighter text-slate-900">
          Every capability, by plan.
        </h2>
        <div className="mt-10 rounded-2xl exos-card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-4 font-bold text-slate-600 uppercase tracking-widest text-[11px] min-w-[220px]">
                  Capability
                </th>
                {TIERS.map((t) => (
                  <th
                    key={t.slug}
                    className={`text-center px-4 py-4 font-bold text-slate-900 ${
                      t.highlighted ? "bg-emerald-500/5" : ""
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: ACCENT[t.slug].dot, boxShadow: `0 0 6px ${ACCENT[t.slug].dot}` }}
                      />
                      <span className="font-display tracking-tight">{t.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_MATRIX.map((sec) => (
                <React.Fragment key={sec.section}>
                  <tr className="bg-slate-50/70 border-t border-slate-200">
                    <td colSpan={5} className="px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-600">
                      {sec.section}
                    </td>
                  </tr>
                  {sec.rows.map(([label, a, b, c, d]) => (
                    <tr key={label} className="border-t border-slate-200/60 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-slate-700">{label}</td>
                      <td className="text-center px-4 py-3"><Cell v={a} /></td>
                      <td className="text-center px-4 py-3"><Cell v={b} /></td>
                      <td className="text-center px-4 py-3 bg-emerald-500/5"><Cell v={c} /></td>
                      <td className="text-center px-4 py-3"><Cell v={d} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 text-sm text-slate-600 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          Team plan unlocks role-based permissions + API access — perfect for brokerages running multiple acquisition specialists on one shared pipeline.
        </div>
      </Section>

      {/* FAQ */}
      <Section tid="pricing-faq" bg="obsidian">
        <span className="exos-eyebrow">FAQ</span>
        <h2 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tighter text-slate-900">
          Common questions.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {FAQ.map(({ q, a }) => (
            <div key={q} className="exos-card p-6 hover:border-emerald-500/30 transition-colors">
              <div className="flex items-start gap-2.5 font-bold text-slate-900">
                <HelpCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{q}</span>
              </div>
              <div className="mt-3 text-sm text-slate-600 leading-relaxed pl-6">{a}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section data-testid="pricing-final-cta" className="relative overflow-hidden exos-spotlight exos-noise">
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
          <span className="exos-eyebrow exos-eyebrow-amber">Ready when you are</span>
          <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter text-slate-900">
            14 days free.<br />
            <span className="text-emerald-600">Zero credit card.</span>
          </h2>
          <p className="mt-6 text-slate-600 text-lg max-w-xl mx-auto">
            Wire in your first property and Jarvis is briefing you tomorrow morning at 7am.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="pricing-final-cta-primary"
              className="exos-btn-primary"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="exos-btn-ghost"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
